"use client"

import * as React from "react"
import { type VariantProps } from "class-variance-authority"
import { cn } from "../lib/utils.ts"
import { ToggleGroup as ToggleGroupPrimitive } from "radix-ui"

import { toggleVariants } from "./toggle.tsx"

/*
 * shadcn/ui ToggleGroup. The `segmented` variant is the design language's Segmented
 * control (components.md): plain text options sized to their labels, no well and no frame;
 * the selected one sits on a soft fill. It switches a view of the same content and never
 * triggers an action.
 */
const ToggleGroupContext = React.createContext<
  VariantProps<typeof toggleVariants> & {
    spacing?: number
    segmented?: boolean
  }
>({
  size: "default",
  variant: "default",
  spacing: 0,
  segmented: false,
})

function ToggleGroup({
  className,
  variant,
  size,
  spacing = 0,
  segmented = false,
  children,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Root> &
  VariantProps<typeof toggleVariants> & {
    spacing?: number
    /** The Segmented look: text options, the selected one on a soft fill. */
    segmented?: boolean
  }) {
  return (
    <ToggleGroupPrimitive.Root
      data-slot="toggle-group"
      data-variant={variant}
      data-size={size}
      data-spacing={spacing}
      data-segmented={segmented || undefined}
      style={{ "--gap": spacing } as React.CSSProperties}
      className={cn(
        "group/toggle-group flex w-fit items-center gap-[--spacing(var(--gap))] rounded-control",
        segmented &&
          "relative inline-flex gap-1",
        className
      )}
      {...props}
    >
      <ToggleGroupContext.Provider value={{ variant, size, spacing, segmented }}>
        {children}
      </ToggleGroupContext.Provider>
    </ToggleGroupPrimitive.Root>
  )
}

function ToggleGroupItem({
  className,
  children,
  variant,
  size,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Item> &
  VariantProps<typeof toggleVariants>) {
  const context = React.useContext(ToggleGroupContext)

  return (
    <ToggleGroupPrimitive.Item
      data-slot="toggle-group-item"
      data-variant={context.variant || variant}
      data-size={context.size || size}
      data-spacing={context.spacing}
      className={cn(
        toggleVariants({
          variant: context.variant || variant,
          size: context.size || size,
        }),
        "w-auto min-w-0 shrink-0 px-3 focus:z-(--z-sticky) focus-visible:z-(--z-sticky)",
        !context.segmented &&
          "data-[spacing=0]:rounded-none data-[spacing=0]:first:rounded-l-control data-[spacing=0]:last:rounded-r-control data-[spacing=0]:data-[variant=outline]:border-l-0 data-[spacing=0]:data-[variant=outline]:first:border-l",
        context.segmented &&
          "h-full rounded-control px-2 text-fg-subtle transition-colors duration-(--duration-fast) hover:bg-transparent hover:text-fg data-[state=on]:prism-segment-on data-[state=on]:text-fg data-[state=on]:hover:bg-active",
        className
      )}
      {...props}
    >
      {children}
    </ToggleGroupPrimitive.Item>
  )
}

export { ToggleGroup, ToggleGroupItem }
